import { AIComparisonContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";
import NetworkIcon from "@core/network-icon";
import { IconSun, IconSettings } from "@tabler/icons-react";

interface ComparisonTemplateMultiProps {
  content: AIComparisonContent;
  onUpdate: (newContent: AIComparisonContent) => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
};

const headerVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

export function ComparisonTemplateMulti({
  content,
  onUpdate,
}: ComparisonTemplateMultiProps) {
  const defaultIcons = [<IconSun size={48} />, <IconSettings size={48} />];
  const [editingSide, setEditingSide] = useState<number | null>(null);
  const [editingPointIndex, setEditingPointIndex] = useState<number | null>(
    null,
  );
  const [editingField, setEditingField] = useState<
    "title" | "point" | "mainTitle" | null
  >(null);
  const [tempValue, setTempValue] = useState<string>("");

  // Validate content structure
  if (!content.sides || content.sides.length < 2 || !content.sides[0].points) {
    return (
      <div className="p-4 text-center text-error">
        Invalid comparison data structure.
      </div>
    );
  }

  // Update handlers
  const handleMainTitleUpdate = (newTitle: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    newContent.title = newTitle;
    onUpdate(newContent);
  };

  const handleSideTitleUpdate = (sideIndex: number, newTitle: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.sides && newContent.sides[sideIndex]) {
      newContent.sides[sideIndex].title = newTitle;
      onUpdate(newContent);
    }
  };

  const handlePointUpdate = (
    sideIndex: number,
    pointIndex: number,
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (
      newContent.sides &&
      newContent.sides[sideIndex] &&
      newContent.sides[sideIndex].points
    ) {
      newContent.sides[sideIndex].points[pointIndex] = newText;
      onUpdate(newContent);
    }
  };

  // Get aspect text (for compatibility, though not used directly here)
  const getAspectText = (aspectIndex: number) => {
    if (Array.isArray(content.aspects) && content.aspects[aspectIndex]) {
      const aspect = content.aspects[aspectIndex];
      return typeof aspect === "string"
        ? aspect
        : aspect.title || `Stage ${aspectIndex + 1}`;
    } else if (content.aspects && content.aspects[aspectIndex]) {
      return content.aspects[aspectIndex].title || `Stage ${aspectIndex + 1}`;
    }
    return `Stage ${aspectIndex + 1}`;
  };

  return (
    <motion.div
      className="p-6"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <svg
        width="792"
        height="744"
        viewBox="0 0 792 744"
        className="mx-auto overflow-visible"
        style={{
          fill: "none",
          stroke: "none",
          fillRule: "evenodd",
          clipRule: "evenodd",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeMiterlimit: 1.5,
        }}
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        {/* Central Background */}
        <g
          id="g-root-ro_vah47n10yewi0-stroke-fill"
          data-item-order="-27000"
          transform="translate(290, 233)"
        >
          <g id="ro_vah47n10yewi0-fill" stroke="none" fill="#545454">
            <g>
              <path d="M 34 10L 166 10C 166 10 190 10 190 34L 190 136C 190 136 190 160 166 160L 34 160C 34 160 10 160 10 136L 10 34C 10 34 10 10 34 10"></path>
            </g>
          </g>
          <g
            id="ro_vah47n10yewi0-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#171717"
            strokeWidth="2"
          >
            <g>
              <path d="M 34 10L 166 10C 166 10 190 10 190 34L 190 136C 190 136 190 160 166 160L 34 160C 34 160 10 160 10 136L 10 34C 10 34 10 10 34 10"></path>
            </g>
          </g>
          <g
            id="ro_vah47n10yewi0-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#3131311a"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 34 10L 166 10C 166 10 190 10 190 34L 190 136C 190 136 190 160 166 160L 34 160C 34 160 10 160 10 136L 10 34C 10 34 10 10 34 10"></path>
            </g>
          </g>
        </g>

        {/* İslam Medeniyeti */}
        <g
          id="g-root-ro_islam-stroke-fill"
          data-item-order="-11664"
          transform="translate(26, 134)"
        >
          <g
            id="ro_islam-fill"
            stroke="none"
            fill={`${content.sides[0].color}50`}
          >
            <g>
              <path d="M 22 10L 214 10C 214 10 226 10 226 22L 226 52C 226 52 226 64 214 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
            </g>
          </g>
          <g
            id="ro_islam-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={`${content.sides[0].color}70`}
            strokeWidth="2"
          >
            <g>
              <path d="M 22 10L 214 10C 214 10 226 10 226 22L 226 52C 226 52 226 64 214 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
            </g>
          </g>
          <g
            id="ro_islam-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={`${content.sides[0].color}70`}
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 22 10L 214 10C 214 10 226 10 226 22L 226 52C 226 52 226 64 214 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
            </g>
          </g>
        </g>
        {/* İslam Medeniyeti Step 1 */}
        <g
          id="g-root-tx_islam_step1-stroke-fill"
          data-item-order="0"
          transform="translate(0, 200)"
        >
          <foreignObject x="12.38" y="10" width="200" height="50">
            {editingField === "point" &&
            editingSide === 0 &&
            editingPointIndex === 0 ? (
              <textarea
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  setEditingPointIndex(null);
                  if (tempValue !== content.sides[0].points[0]) {
                    handlePointUpdate(0, 0, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.currentTarget.blur();
                  }
                }}
                rows={1}
                className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                  lineHeight: "1.5",
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onFocus={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("point");
                  setEditingSide(0);
                  setEditingPointIndex(0);
                  setTempValue(content.sides[0].points[0] || "-");
                }}
                className="block cursor-text min-h-[24px] text-right mr-2 mt-1 rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                }}
              >
                {content.sides[0].points[0] || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* İslam Medeniyeti Step 2 */}
        <g
          id="g-root-tx_islam_step2-stroke-fill"
          data-item-order="0"
          transform="translate(0, 250)"
        >
          <foreignObject x="13.97" y="10" width="200" height="50">
            {editingField === "point" &&
            editingSide === 0 &&
            editingPointIndex === 1 ? (
              <textarea
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  setEditingPointIndex(null);
                  if (tempValue !== content.sides[0].points[1]) {
                    handlePointUpdate(0, 1, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.currentTarget.blur();
                  }
                }}
                rows={1}
                className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                  lineHeight: "1.5",
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onFocus={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("point");
                  setEditingSide(0);
                  setEditingPointIndex(1);
                  setTempValue(content.sides[0].points[1] || "-");
                }}
                className="block text-right mr-2 mt-1 cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                }}
              >
                {content.sides[0].points[1] || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* İslam Medeniyeti Step 3 */}
        <g
          id="g-root-tx_islam_step3-stroke-fill"
          data-item-order="0"
          transform="translate(0, 300)"
        >
          <foreignObject x="17.01" y="10" width="200" height="50">
            {editingField === "point" &&
            editingSide === 0 &&
            editingPointIndex === 2 ? (
              <textarea
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  setEditingPointIndex(null);
                  if (tempValue !== content.sides[0].points[2]) {
                    handlePointUpdate(0, 2, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.currentTarget.blur();
                  }
                }}
                rows={1}
                className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                  lineHeight: "1.5",
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onFocus={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("point");
                  setEditingSide(0);
                  setEditingPointIndex(2);
                  setTempValue(content.sides[0].points[2] || "-");
                }}
                className="block cursor-text text-right pr-4 min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                }}
              >
                {content.sides[0].points[2] || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* İslam Medeniyeti Uzun Çizgi */}
        <g
          id="g-root-cu_islam_main_line-stroke-fill"
          data-item-order="0"
          transform="translate(242, 161)"
        >
          <g
            id="cu_islam_main_line-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 58 132L 55 132L 54.9 132C 43.357252 132.000001 34.000004 122.642753 34.000004 111.100002L 34 111L 34 30.9C 33.999998 19.357247 24.642749 9.999998 13.099998 9.999998L 13 10L 10 10"></path>
            </g>
          </g>
          <g
            id="cu_islam_main_line-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 58 132L 55 132L 54.9 132C 43.357252 132.000001 34.000004 122.642753 34.000004 111.100002L 34 111L 34 30.9C 33.999998 19.357247 24.642749 9.999998 13.099998 9.999998L 13 10L 10 10"></path>
            </g>
          </g>
        </g>
        {/* İslam Medeniyeti Step 1 Line */}
        <g
          id="g-root-cu_islam_line1-stroke-fill"
          data-item-order="0"
          transform="translate(200, 188)"
        >
          <g
            id="cu_islam_line1-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 28 10L 28 22L 28 34L 16 34L 4 34"></path>
            </g>
          </g>
          <g
            id="cu_islam_line1-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 28 10L 28 22L 28 34L 16 34L 4 34"></path>
            </g>
          </g>
        </g>
        {/* İslam Medeniyeti Step 2 Line */}
        <g
          id="g-root-cu_islam_line2-stroke-fill"
          data-item-order="0"
          transform="translate(200, 204)"
        >
          <g
            id="cu_islam_line2-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 28 10L 28 40L 28 70L 16 70L 4 70"></path>
            </g>
          </g>
          <g
            id="cu_islam_line2-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 28 10L 28 40L 28 70L 16 70L 4 70"></path>
            </g>
          </g>
        </g>
        {/* İslam Medeniyeti Step 3 Line */}
        <g
          id="g-root-cu_islam_line3-stroke-fill"
          data-item-order="0"
          transform="translate(200, 214)"
        >
          <g
            id="cu_islam_line3-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 28 10L 28 22L 28 94C 27.999987 100.627412 22.627403 105.999995 15.999986 105.999995L 15.9 106L 4 106"></path>
            </g>
          </g>
          <g
            id="cu_islam_line3-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 28 10L 28 22L 28 94C 27.999987 100.627412 22.627403 105.999995 15.999986 105.999995L 15.9 106L 4 106"></path>
            </g>
          </g>
        </g>
        {/* İslam Medeniyeti Yazı */}
        <g
          id="g-root-tx_islam_title-stroke-fill"
          data-item-order="1000000000"
          transform="translate(38, 149)"
        >
          <foreignObject x="12.39" y="10" width="200" height="50">
            {editingField === "title" && editingSide === 0 ? (
              <input
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  if (tempValue !== content.sides[0].title) {
                    handleSideTitleUpdate(0, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="px-2 py-1 rounded bg-base-100 outline-none ring-2 ring-primary w-full transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("title");
                  setEditingSide(0);
                  setTempValue(content.sides[0].title || "-");
                }}
                className="block cursor-text min-h-[24px] text-right pr-14 rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[0].color,
                }}
              >
                {content.sides[0].title || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* İslam Medeniyeti Icon */}
        <g
          id="g-root-ic_islam_icon-stroke-fill"
          data-item-order="1000000000"
          transform="translate(200, 146)"
        >
          <foreignObject x="0" y="0" width="60" height="60">
            <NetworkIcon
              className="ml-3 mt-2"
              value={content.sides[0].iconId || "alert-circle"}
              type="LUCIDE"
              style={{
                color: content.sides[0].color || "#a6da37",
                width: "30px",
                height: "30px",
              }}
            />
          </foreignObject>
        </g>

        {/* Batı Avrupa */}
        <g
          id="g-root-ro_bati_avrupa-stroke-fill"
          data-item-order="-9072"
          transform="translate(518, 134)"
        >
          <g
            id="ro_bati_avrupa-fill"
            stroke="none"
            fill={`${content.sides[1].color}50`}
          >
            <g>
              <path d="M 32 10L 245 10C 245 10 263 10 263 22L 263 52C 263 52 263 64 245 64L 32 64C 32 64 15 64 15 52L 15 22C 15 22 15 10 32 10"></path>
            </g>
          </g>
          <g
            id="ro_bati_avrupa-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={`${content.sides[1].color}70`}
            strokeWidth="2"
          >
            <g>
              <path d="M 32 10L 245 10C 245 10 263 10 263 22L 263 52C 263 52 263 64 245 64L 32 64C 32 64 15 64 15 52L 15 22C 15 22 15 10 32 10"></path>
            </g>
          </g>
          <g
            id="ro_bati_avrupa-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={`${content.sides[1].color}70`}
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 32 10L 245 10C 245 10 263 10 263 22L 263 52C 263 52 263 64 245 64L 32 64C 32 64 15 64 15 52L 15 22C 15 22 15 10 32 10"></path>
            </g>
          </g>
        </g>
        {/* Batı Avrupa Step 1 */}
        <g
          id="g-root-tx_bati_step1-stroke-fill"
          data-item-order="0"
          transform="translate(566, 200)"
        >
          <foreignObject x="13.31" y="10" width="200" height="50">
            {editingField === "point" &&
            editingSide === 1 &&
            editingPointIndex === 0 ? (
              <textarea
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  setEditingPointIndex(null);
                  if (tempValue !== content.sides[1].points[0]) {
                    handlePointUpdate(1, 0, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.currentTarget.blur();
                  }
                }}
                rows={1}
                className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                  lineHeight: "1.5",
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onFocus={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("point");
                  setEditingSide(1);
                  setEditingPointIndex(0);
                  setTempValue(content.sides[1].points[0] || "-");
                }}
                className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                }}
              >
                {content.sides[1].points[0] || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* Batı Avrupa Step 2 */}
        <g
          id="g-root-tx_bati_step2-stroke-fill"
          data-item-order="0"
          transform="translate(566, 250)"
        >
          <foreignObject x="15.68" y="10" width="200" height="50">
            {editingField === "point" &&
            editingSide === 1 &&
            editingPointIndex === 1 ? (
              <textarea
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  setEditingPointIndex(null);
                  if (tempValue !== content.sides[1].points[1]) {
                    handlePointUpdate(1, 1, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.currentTarget.blur();
                  }
                }}
                rows={1}
                className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                  lineHeight: "1.5",
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onFocus={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("point");
                  setEditingSide(1);
                  setEditingPointIndex(1);
                  setTempValue(content.sides[1].points[1] || "-");
                }}
                className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                }}
              >
                {content.sides[1].points[1] || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* Batı Avrupa Step 3 */}
        <g
          id="g-root-tx_bati_step3-stroke-fill"
          data-item-order="0"
          transform="translate(566, 300)"
        >
          <foreignObject x="14.8" y="10" width="200" height="50">
            {editingField === "point" &&
            editingSide === 1 &&
            editingPointIndex === 2 ? (
              <textarea
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  setEditingPointIndex(null);
                  if (tempValue !== content.sides[1].points[2]) {
                    handlePointUpdate(1, 2, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.currentTarget.blur();
                  }
                }}
                rows={1}
                className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                  lineHeight: "1.5",
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onFocus={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("point");
                  setEditingSide(1);
                  setEditingPointIndex(2);
                  setTempValue(content.sides[1].points[2] || "-");
                }}
                className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                }}
              >
                {content.sides[1].points[2] || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* Batı Avrupa Uzun Çizgi */}
        <g
          id="g-root-cu_bati_main_line-stroke-fill"
          data-item-order="0"
          transform="translate(470, 161)"
        >
          <g
            id="cu_bati_main_line-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 10 132L 13 132L 13.1 132C 24.642748 132.000001 33.999996 122.642753 33.999996 111.100002L 34 111L 34 30.9C 34.000002 19.357247 43.357251 9.999998 54.900002 9.999998L 55 10L 58 10"></path>
            </g>
          </g>
          <g
            id="cu_bati_main_line-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 10 132L 13 132L 13.1 132C 24.642748 132.000001 33.999996 122.642753 33.999996 111.100002L 34 111L 34 30.9C 34.000002 19.357247 43.357251 9.999998 54.900002 9.999998L 55 10L 58 10"></path>
            </g>
          </g>
        </g>
        {/* Batı Avrupa Step 1 Line */}
        <g
          id="g-root-cu_bati_line1-stroke-fill"
          data-item-order="0"
          transform="translate(542, 188)"
        >
          <g
            id="cu_bati_line1-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 10 10L 10 22L 10 34L 22 34L 34 34"></path>
            </g>
          </g>
          <g
            id="cu_bati_line1-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 10 10L 10 22L 10 34L 22 34L 34 34"></path>
            </g>
          </g>
        </g>
        {/* Batı Avrupa Step 2 Line */}
        <g
          id="g-root-cu_bati_line2-stroke-fill"
          data-item-order="0"
          transform="translate(542, 204)"
        >
          <g
            id="cu_bati_line2-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 10 10L 10 40L 10 70L 22 70L 34 70"></path>
            </g>
          </g>
          <g
            id="cu_bati_line2-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 10 10L 10 40L 10 70L 22 70L 34 70"></path>
            </g>
          </g>
        </g>
        {/* Batı Avrupa Step 3 Line */}
        <g
          id="g-root-cu_bati_line3-stroke-fill"
          data-item-order="0"
          transform="translate(542, 214)"
        >
          <g
            id="cu_bati_line3-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
            strokeDasharray="5.0, 7.0"
          >
            <g>
              <path d="M 10 10L 10 22L 10 94C 10.000013 100.627412 15.372597 105.999995 22.000014 105.999995L 22.1 106L 34 106"></path>
            </g>
          </g>
          <g
            id="cu_bati_line3-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 10 10L 10 22L 10 94C 10.000013 100.627412 15.372597 105.999995 22.000014 105.999995L 22.1 106L 34 106"></path>
            </g>
          </g>
        </g>
        {/* Batı Avrupa Yazı */}
        <g
          id="g-root-tx_bati_title-stroke-fill"
          data-item-order="1000000000"
          transform="translate(560, 149)"
        >
          <foreignObject x="13.02" y="10" width="200" height="50">
            {editingField === "title" && editingSide === 1 ? (
              <input
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  setEditingSide(null);
                  if (tempValue !== content.sides[1].title) {
                    handleSideTitleUpdate(1, tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="px-2 py-1 rounded bg-base-100 outline-none ring-2 ring-primary w-full transition-all"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                }}
              />
            ) : (
              <span
                onClick={() => {
                  setEditingField("title");
                  setEditingSide(1);
                  setTempValue(content.sides[1].title || "-");
                }}
                className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                style={{
                  font: "16px Roboto, sans-serif",
                  color: content.sides[1].color,
                }}
              >
                {content.sides[1].title || "-"}
              </span>
            )}
          </foreignObject>
        </g>
        {/* Batı Avrupa Icon */}
        <g
          id="g-root-ic_bati_icon-stroke-fill"
          data-item-order="1000000000"
          transform="translate(530, 146)"
        >
          <foreignObject x="0" y="0" width="60" height="60">
            <NetworkIcon
              className="ml-3 mt-2"
              value={content.sides[1].iconId || "alert-circle"}
              type="LUCIDE"
              style={{
                color: content.sides[1].color || "#cb68f9",
                width: "30px",
                height: "30px",
              }}
            />
          </foreignObject>
        </g>

        {content.sides.length >= 3 && (
          <>
            {/* Hint Medeniyeti */}
            <g
              id="g-root-ro_hint-stroke-fill"
              data-item-order="-11016"
              transform="translate(38, 320)"
            >
              <g
                id="ro_hint-fill"
                stroke="none"
                fill={`${content.sides[2].color}50`}
              >
                <g>
                  <path d="M 22 10L 202 10C 202 10 214 10 214 22L 214 52C 214 52 214 64 202 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
                </g>
              </g>
              <g
                id="ro_hint-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke={`${content.sides[2].color}70`}
                strokeWidth="2"
              >
                <g>
                  <path d="M 22 10L 202 10C 202 10 214 10 214 22L 214 52C 214 52 214 64 202 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
                </g>
              </g>
              <g
                id="ro_hint-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke={`${content.sides[2].color}70`}
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 22 10L 202 10C 202 10 214 10 214 22L 214 52C 214 52 214 64 202 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
                </g>
              </g>
            </g>
            {/* Hint Medeniyeti Step 1 */}
            <g
              id="g-root-tx_hint_step1-stroke-fill"
              data-item-order="0"
              transform="translate(10, 386)"
            >
              <foreignObject x="14.36" y="10" width="200" height="50">
                {editingField === "point" &&
                editingSide === 2 &&
                editingPointIndex === 0 ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      setEditingPointIndex(null);
                      if (tempValue !== content.sides[2].points[0]) {
                        handlePointUpdate(2, 0, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                    rows={1}
                    className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                      lineHeight: "1.5",
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    onFocus={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("point");
                      setEditingSide(2);
                      setEditingPointIndex(0);
                      setTempValue(content.sides[2].points[0] || "-");
                    }}
                    className="block text-right pr-7 pt-1 cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                    }}
                  >
                    {content.sides[2].points[0] || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Hint Medeniyeti Step 2 */}
            <g
              id="g-root-tx_hint_step2-stroke-fill"
              data-item-order="0"
              transform="translate(10, 422)"
            >
              <foreignObject x="16.27" y="10" width="200" height="50">
                {editingField === "point" &&
                editingSide === 2 &&
                editingPointIndex === 1 ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      setEditingPointIndex(null);
                      if (tempValue !== content.sides[2].points[1]) {
                        handlePointUpdate(2, 1, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                    rows={1}
                    className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                      lineHeight: "1.5",
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    onFocus={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("point");
                      setEditingSide(2);
                      setEditingPointIndex(1);
                      setTempValue(content.sides[2].points[1] || "-");
                    }}
                    className="block cursor-text text-right pr-8 py-1 min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                    }}
                  >
                    {content.sides[2].points[1] || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Hint Medeniyeti Step 3 */}
            <g
              id="g-root-tx_hint_step3-stroke-fill"
              data-item-order="0"
              transform="translate(10, 458)"
            >
              <foreignObject x="12.71" y="10" width="200" height="50">
                {editingField === "point" &&
                editingSide === 2 &&
                editingPointIndex === 2 ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      setEditingPointIndex(null);
                      if (tempValue !== content.sides[2].points[2]) {
                        handlePointUpdate(2, 2, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                    rows={1}
                    className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                      lineHeight: "1.5",
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    onFocus={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("point");
                      setEditingSide(2);
                      setEditingPointIndex(2);
                      setTempValue(content.sides[2].points[2] || "-");
                    }}
                    className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                    }}
                  >
                    {content.sides[2].points[2] || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Hint Medeniyeti Uzun Çizgi */}
            <g
              id="g-root-cu_hint_main_line-stroke-fill"
              data-item-order="0"
              transform="translate(242, 333)"
            >
              <g
                id="cu_hint_main_line-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 58 10L 55 10L 54.9 10C 51.522849 10.003932 47.800583 10.61303 43.899999 11.800002C 41.565212 12.503609 38.06312 14.343091 33.999998 16.999996C 29.936876 19.656902 26.434785 21.496384 24.099998 22.199996C 20.199414 23.386975 16.477148 23.996073 13.099998 24.000003L 13 24L 10 24"></path>
                </g>
              </g>
              <g
                id="cu_hint_main_line-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 58 10L 55 10L 54.9 10C 51.522849 10.003932 47.800583 10.61303 43.899999 11.800002C 41.565212 12.503609 38.06312 14.343091 33.999998 16.999996C 29.936876 19.656902 26.434785 21.496384 24.099998 22.199996C 20.199414 23.386975 16.477148 23.996073 13.099998 24.000003L 13 24L 10 24"></path>
                </g>
              </g>
            </g>
            {/* Hint Medeniyeti Step 1 Line */}
            <g
              id="g-root-cu_hint_line1-stroke-fill"
              data-item-order="0"
              transform="translate(200, 374)"
            >
              <g
                id="cu_hint_line1-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 28 10L 28 22L 28 34L 16 34L 4 34"></path>
                </g>
              </g>
              <g
                id="cu_hint_line1-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 28 10L 28 22L 28 34L 16 34L 4 34"></path>
                </g>
              </g>
            </g>
            {/* Hint Medeniyeti Step 2 Line */}
            <g
              id="g-root-cu_hint_line2-stroke-fill"
              data-item-order="0"
              transform="translate(200, 374)"
            >
              <g
                id="cu_hint_line2-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 28 10L 28 40L 28 70L 16 70L 4 70"></path>
                </g>
              </g>
              <g
                id="cu_hint_line2-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 28 10L 28 40L 28 70L 16 70L 4 70"></path>
                </g>
              </g>
            </g>
            {/* Hint Medeniyeti Step 3 Line */}
            <g
              id="g-root-cu_hint_line3-stroke-fill"
              data-item-order="0"
              transform="translate(200, 374)"
            >
              <g
                id="cu_hint_line3-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 28 10L 28 22L 28 94C 27.999989 100.627431 22.627405 106.000014 15.999987 106.000014L 15.9 106L 4 106"></path>
                </g>
              </g>
              <g
                id="cu_hint_line3-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 28 10L 28 22L 28 94C 27.999989 100.627431 22.627405 106.000014 15.999987 106.000014L 15.9 106L 4 106"></path>
                </g>
              </g>
            </g>
            {/* Hint Medeniyeti Yazı */}
            <g
              id="g-root-tx_hint_title-stroke-fill"
              data-item-order="1000000000"
              transform="translate(50, 335)"
            >
              <foreignObject x="12.55" y="10" width="200" height="50">
                {editingField === "title" && editingSide === 2 ? (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      if (tempValue !== content.sides[2].title) {
                        handleSideTitleUpdate(2, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    className="px-2 py-1 rounded bg-base-100 outline-none ring-2 ring-primary w-full transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("title");
                      setEditingSide(2);
                      setTempValue(content.sides[2].title || "-");
                    }}
                    className="block cursor-text min-h-[24px] text-right pr-14 rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[2].color,
                    }}
                  >
                    {content.sides[2].title || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Hint Medeniyeti Icon */}
            <g
              id="g-root-ic_hint_icon-stroke-fill"
              data-item-order="1000000000"
              transform="translate(200, 332)"
            >
              <foreignObject x="0" y="0" width="60" height="60">
                <NetworkIcon
                  className="ml-3 mt-2"
                  value={content.sides[2].iconId || "alert-circle"}
                  type="LUCIDE"
                  style={{
                    color: content.sides[2].color || "#f366bb",
                    width: "30px",
                    height: "30px",
                  }}
                />
              </foreignObject>
            </g>
          </>
        )}

        {content.sides.length >= 4 && (
          <>
            {/* Bizans Medeniyeti */}
            <g
              id="g-root-ro_bizans-stroke-fill"
              data-item-order="-12312"
              transform="translate(518, 320)"
            >
              <g
                id="ro_bizans-fill"
                stroke="none"
                fill={`${content.sides[3].color}50`}
              >
                <g>
                  <path d="M 22 10L 226 10C 226 10 238 10 238 22L 238 52C 238 52 238 64 226 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
                </g>
              </g>
              <g
                id="ro_bizans-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke={`${content.sides[3].color}70`}
                strokeWidth="2"
              >
                <g>
                  <path d="M 22 10L 226 10C 226 10 238 10 238 22L 238 52C 238 52 238 64 226 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
                </g>
              </g>
              <g
                id="ro_bizans-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke={`${content.sides[3].color}70`}
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 22 10L 226 10C 226 10 238 10 238 22L 238 52C 238 52 238 64 226 64L 22 64C 22 64 10 64 10 52L 10 22C 10 22 10 10 22 10"></path>
                </g>
              </g>
            </g>
            {/* Bizans Medeniyeti Step 1 */}
            <g
              id="g-root-tx_bizans_step1-stroke-fill"
              data-item-order="0"
              transform="translate(566, 386)"
            >
              <foreignObject x="16.67" y="10" width="200" height="50">
                {editingField === "point" &&
                editingSide === 3 &&
                editingPointIndex === 0 ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      setEditingPointIndex(null);
                      if (tempValue !== content.sides[3].points[0]) {
                        handlePointUpdate(3, 0, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                    rows={1}
                    className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[3].color,
                      lineHeight: "1.5",
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    onFocus={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("point");
                      setEditingSide(3);
                      setEditingPointIndex(0);
                      setTempValue(content.sides[3].points[0] || "-");
                    }}
                    className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[3].color,
                    }}
                  >
                    {content.sides[3].points[0] || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Bizans Medeniyeti Step 2 */}
            <g
              id="g-root-tx_bizans_step2-stroke-fill"
              data-item-order="0"
              transform="translate(566, 422)"
            >
              <foreignObject x="15.68" y="10" width="200" height="50">
                {editingField === "point" &&
                editingSide === 3 &&
                editingPointIndex === 1 ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      setEditingPointIndex(null);
                      if (tempValue !== content.sides[3].points[1]) {
                        handlePointUpdate(3, 1, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                    rows={1}
                    className="w-full p-1 bg-transparent resize-none outline-none border-none focus:ring-1 focus:ring-primary/40 rounded text-base-content/90 transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[3].color,
                      lineHeight: "1.5",
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    onFocus={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("point");
                      setEditingSide(3);
                      setEditingPointIndex(1);
                      setTempValue(content.sides[3].points[1] || "-");
                    }}
                    className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[3].color,
                    }}
                  >
                    {content.sides[3].points[1] || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Bizans Medeniyeti Step 3 */}
            <g
              id="g-root-cu_bizans_main_line-stroke-fill"
              data-item-order="0"
              transform="translate(470, 333)"
            >
              <g
                id="cu_bizans_main_line-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 10 10L 13 10L 13.1 10C 16.477151 10.003932 20.199417 10.61303 24.100001 11.800002C 26.434788 12.503609 29.93688 14.343091 34.000002 16.999996C 38.063124 19.656902 41.565215 21.496384 43.900002 22.199996C 47.800586 23.386975 51.522852 23.996073 54.900002 24.000003L 55 24L 58 24"></path>
                </g>
              </g>
              <g
                id="cu_bizans_main_line-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 10 10L 13 10L 13.1 10C 16.477151 10.003932 20.199417 10.61303 24.100001 11.800002C 26.434788 12.503609 29.93688 14.343091 34.000002 16.999996C 38.063124 19.656902 41.565215 21.496384 43.900002 22.199996C 47.800586 23.386975 51.522852 23.996073 54.900002 24.000003L 55 24L 58 24"></path>
                </g>
              </g>
            </g>
            {/* Bizans Medeniyeti Step 1 Line */}
            <g
              id="g-root-cu_bizans_line1-stroke-fill"
              data-item-order="0"
              transform="translate(542, 374)"
            >
              <g
                id="cu_bizans_line1-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 10 10L 10 22L 10 34L 22 34L 34 34"></path>
                </g>
              </g>
              <g
                id="cu_bizans_line1-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 10 10L 10 22L 10 34L 22 34L 34 34"></path>
                </g>
              </g>
            </g>
            {/* Bizans Medeniyeti Step 2 Line */}
            <g
              id="g-root-cu_bizans_line2-stroke-fill"
              data-item-order="0"
              transform="translate(542, 374)"
            >
              <g
                id="cu_bizans_line2-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 10 10L 10 40L 10 70L 22 70L 34 70"></path>
                </g>
              </g>
              <g
                id="cu_bizans_line2-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 10 10L 10 40L 10 70L 22 70L 34 70"></path>
                </g>
              </g>
            </g>
            {/* Bizans Medeniyeti Step 3 Line */}
            <g
              id="g-root-cu_bizans_line3-stroke-fill"
              data-item-order="0"
              transform="translate(542, 374)"
            >
              <g
                id="cu_bizans_line3-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#b7b7b7"
                strokeWidth="2"
                strokeDasharray="5.0, 7.0"
              >
                <g>
                  <path d="M 10 10L 10 22L 10 94C 10.000011 100.627431 15.372595 106.000014 22.000013 106.000014L 22.1 106L 34 106"></path>
                </g>
              </g>
              <g
                id="cu_bizans_line3-stroke-overlay"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="4"
                stroke="#0000001b"
                strokeWidth="2"
                strokeDasharray="4,6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="30;0"
                  dur="1s"
                  repeatCount="indefinite"
                  begin="0"
                ></animate>
                <g>
                  <path d="M 10 10L 10 22L 10 94C 10.000011 100.627431 15.372595 106.000014 22.000013 106.000014L 22.1 106L 34 106"></path>
                </g>
              </g>
            </g>
            {/* Bizans Medeniyeti Yazı */}
            <g
              id="g-root-tx_bizans_title-stroke-fill"
              data-item-order="1000000000"
              transform="translate(566, 335)"
            >
              <foreignObject x="13.17" y="10" width="200" height="50">
                {editingField === "title" && editingSide === 3 ? (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingSide(null);
                      if (tempValue !== content.sides[3].title) {
                        handleSideTitleUpdate(3, tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    className="px-2 py-1 rounded bg-base-100 outline-none ring-2 ring-primary w-full transition-all"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[3].color,
                    }}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingField("title");
                      setEditingSide(3);
                      setTempValue(content.sides[3].title || "-");
                    }}
                    className="block cursor-text min-h-[24px] rounded px-1 hover:bg-base-300/20 transition-colors"
                    style={{
                      font: "16px Roboto, sans-serif",
                      color: content.sides[3].color,
                    }}
                  >
                    {content.sides[3].title || "-"}
                  </span>
                )}
              </foreignObject>
            </g>
            {/* Bizans Medeniyeti Icon */}
            <g
              id="g-root-ic_bizans_icon-stroke-fill"
              data-item-order="1000000000"
              transform="translate(530, 332)"
            >
              <foreignObject x="0" y="0" width="60" height="60">
                <NetworkIcon
                  className="ml-3 mt-2"
                  value={content.sides[3].iconId || "alert-circle"}
                  type="LUCIDE"
                  style={{
                    color: content.sides[3].color || "#ffe711",
                    width: "30px",
                    height: "30px",
                  }}
                />
              </foreignObject>
            </g>
          </>
        )}

        {/* Title */}
        <g
          id="g-root-tx_ortaamed_v3lw9f10yyjgm-stroke-fill"
          data-item-order="0"
          transform="translate(50, 50)"
        >
          <foreignObject x="12.24" y="10" width="600" height="50">
            {editingField === "mainTitle" ? (
              <input
                autoFocus
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => {
                  setEditingField(null);
                  if (tempValue !== content.title) {
                    handleMainTitleUpdate(tempValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="px-2 py-1 rounded bg-base-100 outline-none ring-2 ring-primary w-full text-center transition-all"
                style={{
                  font: "bold 25px Roboto, sans-serif",
                  color: "#171717",
                }}
              />
            ) : (
              <div
                className="w-full text-center"
                onClick={() => {
                  setEditingField("mainTitle");
                  setTempValue(content.title);
                }}
                style={{
                  font: "bold 25px Roboto, sans-serif",
                  color: "#171717",
                }}
              >
                {content.title}
              </div>
            )}
          </foreignObject>
        </g>
        {/* Orta Çağ Medeniyetleri Title */}
        <g
          id="g-root-tx_ortaamed_1d1r4pf10yeu4u-stroke-fill"
          data-item-order="1000000000"
          transform="translate(314, 311)"
        >
          <g id="tx_ortaamed_1d1r4pf10yeu4u-fill" stroke="none" fill="#b7b7b7">
            <g>
              <text
                style={{ font: "14px Roboto, sans-serif", whiteSpace: "pre" }}
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="12" y="33.5" dominantBaseline="ideographic">
                  {content.title}
                </tspan>
              </text>
            </g>
          </g>
        </g>

        {/* Central Icon */}
        <g
          id="g-root-ic_side_zqamc310yevpr-stroke-fill"
          data-item-order="1000000000"
          transform="translate(356, 257)"
        >
          <g
            id="ic_side_zqamc310yevpr-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#b7b7b7"
            strokeWidth="2"
          >
            <g>
              <path d="M 21.781 55.2668L 21.781 46.759399C 18.932159 45.305599 16.522221 43.118801 14.79928 40.424202C 13.07634 37.729599 12.10264 34.624599 11.978426 31.4286C 11.854204 28.2327 12.583941 25.06142 14.09254 22.24122C 15.60112 19.421021 17.834061 17.053801 20.561459 15.38328C 23.288841 13.71274 26.41218 12.79924 29.6099 12.73682C 32.807602 12.6744 35.964199 13.465321 38.754799 15.02814C 41.5452 16.59096 43.868801 18.86924 45.486401 21.62842C 46.645802 23.6063 47.412399 25.780079 47.753799 28.030661M 38.141399 55.266602L 38.141399 47.086399L 41.625198 47.086399M 52.682201 37.692398C 52.682201 33.646202 52.062401 31.5938 47.75 28C 44.240799 29.98254 41.623001 33.5214 41.623001 37.134598L 41.623001 47.104401C 45.812401 47.104401 49.367199 47.3694 49.367199 44.874599C 49.367199 44.023399 49.316399 42.364201 49.5 41.6562C 49.855 40.287003 50.523399 39.539001 51.5312 38.5938C 51.874199 38.295601 52.2188 38.023399 52.682201 37.692398ZM 52.617599 46.2976L 55.998199 46.2976L 55.998199 43.091801C 55.998199 41.232597 55.555401 39.771999 52.681999 37.687599C 49.808399 39.771999 49.365799 41.232597 49.365799 43.091801L 49.365601 46.2976L 52.746399 46.2976M 10 10M 52.645599 46.297798L 52.645599 55.057598M 12.125 28.172359C 19.204201 34.584 29.82424 38.559402 41.623001 38.559402M 47.9576 33.366199L 50.923599 33.366199"></path>
            </g>
          </g>
          <g
            id="ic_side_zqamc310yevpr-stroke-overlay"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#0000001b"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <g>
              <path d="M 21.781 55.2668L 21.781 46.759399C 18.932159 45.305599 16.522221 43.118801 14.79928 40.424202C 13.07634 37.729599 12.10264 34.624599 11.978426 31.4286C 11.854204 28.2327 12.583941 25.06142 14.09254 22.24122C 15.60112 19.421021 17.834061 17.053801 20.561459 15.38328C 23.288841 13.71274 26.41218 12.79924 29.6099 12.73682C 32.807602 12.6744 35.964199 13.465321 38.754799 15.02814C 41.5452 16.59096 43.868801 18.86924 45.486401 21.62842C 46.645802 23.6063 47.412399 25.780079 47.753799 28.030661M 38.141399 55.266602L 38.141399 47.086399L 41.625198 47.086399M 52.682201 37.692398C 52.682201 33.646202 52.062401 31.5938 47.75 28C 44.240799 29.98254 41.623001 33.5214 41.623001 37.134598L 41.623001 47.104401C 45.812401 47.104401 49.367199 47.3694 49.367199 44.874599C 49.367199 44.023399 49.316399 42.364201 49.5 41.6562C 49.855 40.287003 50.523399 39.539001 51.5312 38.5938C 51.874199 38.295601 52.2188 38.023399 52.682201 37.692398ZM 52.617599 46.2976L 55.998199 46.2976L 55.998199 43.091801C 55.998199 41.232597 55.555401 39.771999 52.681999 37.687599C 49.808399 39.771999 49.365799 41.232597 49.365799 43.091801L 49.365601 46.2976L 52.746399 46.2976M 10 10M 52.645599 46.297798L 52.645599 55.057598M 12.125 28.172359C 19.204201 34.584 29.82424 38.559402 41.623001 38.559402M 47.9576 33.366199L 50.923599 33.366199"></path>
            </g>
          </g>
        </g>
      </svg>
    </motion.div>
  );
}
